import { createAppSlice } from "../../app/createAppSlice";
import { createSelector, PayloadAction } from "@reduxjs/toolkit";
import { setSelectedSpaceNumberInRedux } from "../designSlice";
import { IAppThunk, IRootState } from "../../app/store";
import {
  convertDevicesToPixPolygons,
  copyDeviceAndMoveOneSquare,
  withWorldZInCoordinate,
} from "./deviceUtils";
import { generateRandomString, getSqaureAroundCoordinate } from "../../utils";
import { DeviceCategoryEnum } from "@multiconsult-group/multiplacer-core";
import { selectProjectNo } from "../../Room/RoomSlice";
import { getCacheKey } from "../../apiRTK";
import { IPixCircle, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { IRoomsSummaryHashmap } from "@backend/src/shared/schemas/schemas";
import { GetMultiplacerDevicetypesApiResponse, openapi } from "../../../openapi";
import { selectDeviceById, selectHttpDevices } from "./deviceSelectors";
import { IDevice } from "@multiconsult-group/multiplacer-core";

export interface IDeviceSliceState {
  savingDevices: boolean;
  selectedDeviceId: string | null;
  deviceIdInCtrlC: string | undefined;
}

const initialState: IDeviceSliceState = {
  savingDevices: false,
  selectedDeviceId: null,
  deviceIdInCtrlC: undefined,
};

export const clickOnDeviceThunk = (device: IDevice): IAppThunk => {
  return async (dispatch) => {
    dispatch(setSelectedDeviceId(device._id as string));
    dispatch(setSelectedSpaceNumberInRedux(device.roomNumber));
  };
};

export const copyDeviceThunk = (deviceId: string | undefined): IAppThunk => {
  return async (dispatch, getState) => {
    if (deviceId == null) {
      return;
    }
    const device = selectDeviceById(getState(), deviceId);
    const floorElevationM = getState().design.floorAndJob?.elevation;

    if (device != null) {
      const newDevice = copyDeviceAndMoveOneSquare(device);
      if (newDevice == null) {
        return;
      }
      newDevice.coordinate = withWorldZInCoordinate(
        newDevice.coordinate,
        floorElevationM,
        newDevice.bottomEdgeHeightAboveFloorMm
      );
      dispatch(
        openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
          body: {
            devices: [newDevice],
          },
        })
      );
    }
  };
};

interface IRotateDeviceArgs {
  device: IDevice;
  angle: number;
}

export const rotateDeviceThunk = (args: IRotateDeviceArgs): IAppThunk => {
  return async (dispatch, getState) => {
    const radian = args.angle * (Math.PI / 180);
    if (args.device == null || args.device?.direction == null) {
      return;
    }
    if (args.device._id == null) {
      return;
    }
    const newDirection = [
      args.device.direction[0] * Math.cos(radian) - args.device.direction[1] * Math.sin(radian),
      args.device?.direction[0] * Math.sin(radian) + args.device?.direction[1] * Math.cos(radian),
    ] as number[];
    const floorElevationM = getState().design.floorAndJob?.elevation;
    dispatch(
      openapi.endpoints.putMultiplacerDeviceUpdate.initiate({
        body: {
          device: {
            ...args.device,
            _id: String(args.device._id),
            coordinate: withWorldZInCoordinate(
              args.device.coordinate,
              floorElevationM,
              args.device.bottomEdgeHeightAboveFloorMm
            ),
            direction: newDirection as [number, number],
          },
        },
      })
    );
  };
};


// If you are not using async thunks you can use the standalone `createSlice`.
export const deviceSlice = createAppSlice({
  name: "device",
  initialState,
  reducers: () => ({
    setSavingDevices: (state, action: PayloadAction<boolean>) => {
      state.savingDevices = action.payload;
    },
    setSelectedDeviceId: (state, action: PayloadAction<string | null>) => {
      state.selectedDeviceId = action.payload;
    },
    setDeviceIdInCtrlCInRedux: (state, action: PayloadAction<{ deviceId: string } | null>) => {
      state.deviceIdInCtrlC = action.payload?.deviceId;
    },
  }),
});

export const selectShowFireDeviceCircles = (state: IRootState) =>
  state.design.showFireDeviceCircles;

export const selectPixCirclesFromDevices = createSelector(
  [
    selectShowFireDeviceCircles,
    (state: IRootState, devices: IDevice[]) => devices, // extra argument utanför state
  ],
  (showFireDeviceCirlces, devices): IPixCircle[] => {
    const fireDevices = devices.filter(
      (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    );
    if (!showFireDeviceCirlces) {
      return [];
    }
    return fireDevices.map((fireDevice) => {
      const circle: IPixCircle = {
        id: generateRandomString(100),
        position: fireDevice.coordinate as number[],
        radius: 1.5,
        color: "#770505",
        opacity: 0.3,
      };
      return circle;
    });
  }
);

export const selectShowFireDeviceSquares = (state: IRootState) => state.design.showFireDeviceSquare;
export const selectFireDeviceCoverSquarePixPolygon = createSelector(
  [
    selectShowFireDeviceSquares,
    (state: IRootState, devices: IDevice[]) => devices, // extra argument utanför state
  ],
  (showFireDeviceCirlces, devices): IPixPolygon[] => {
    const fireDevices = devices.filter(
      (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    );
    if (!showFireDeviceCirlces) {
      return [];
    }
    return fireDevices.map((fireDevice) => {
      const pixPolygon: IPixPolygon = {
        fillColor: "#770505",
        strokeColor: "#000000",
        strokeWidth: "5",
        text: {
          color: "#ffffff",
          fontSize: "0.3",
          show: false,
          values: ["Brannslukningsapparat"],
        },
        isMoveable: false,
        isDragSelectable: false,
        tooltip: "",
        id: generateRandomString(100),
        points: getSqaureAroundCoordinate(fireDevice.coordinate, fireDevice.direction, 3),
      };
      return pixPolygon;
    });
  }
);

interface ILockedAndUnlockedDevices {
  unlockedDevices: IDevice[];
  lockedDevices: IDevice[];
}

const selectedRooms = (state: IRootState) => state.design.selectedRooms;
export const selectDevicesToBeDeletedInSelectedRooms = createSelector(
  [
    selectedRooms,
    (
      state: IRootState,
      roomsAndDevices: {
        rooms: IRoomsSummaryHashmap;
        devices: IDevice[];
      }
    ) => roomsAndDevices,
  ],
  (selectedRooms, roomsAndDevices): ILockedAndUnlockedDevices => {
    let output = {
      unlockedDevices: [] as IDevice[],
      lockedDevices: [] as IDevice[],
    };
    return selectedRooms.reduce(
      (acc, roomNumber) => {
        const roomIsLocked = roomsAndDevices?.rooms[roomNumber];
        const isLocked = roomIsLocked?.multiplacerCeilingDevicesLocked?.bool ?? false;
        const devicesInRoom: IDevice[] = roomsAndDevices.devices.filter(
          (device) => device.roomNumber === roomNumber
        );
        if (isLocked) {
          acc.lockedDevices.push(...devicesInRoom);
        } else {
          acc.unlockedDevices.push(...devicesInRoom);
        }
        return acc;
      },
      { ...output }
    );
  }
);

const selectedSpaceNumber = (state: IRootState) => state.design.selectedSpaceNumber;

export const selectHttpDevicesInRoom = createSelector(
  [selectedSpaceNumber, selectHttpDevices],
  (selectedSpaceNumber, devices): IDevice[] => {
    return devices.filter((device) => device.roomNumber === selectedSpaceNumber);
  }
);

export const selectDevicesExistInRoom = createSelector(
  [selectHttpDevicesInRoom],
  (devicesInRoom): boolean => {
    return devicesInRoom.length > 0;
  }
);

export const selectActiveDeviceInSelectedRoom = createSelector(
  [selectHttpDevicesInRoom, (state: IRootState) => state.device.selectedDeviceId],
  (devicesInRoom, selectedDeviceId): IDevice | undefined => {
    return devicesInRoom.find((device) => device._id === selectedDeviceId);
  }
);

export const selectActiveDeviceIsAirTerminal = createSelector(
  [selectActiveDeviceInSelectedRoom],
  (device): boolean => {
    if (device == null) return false;
    return (
      device.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.RETURN_AIR_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.CHILLER
    );
  }
);

const selectNonPublishedDevices = (state: IRootState) => state.design.nonPublischedDevicesFromAlgos;
const NON_PUBLISHED_DEVICE_OPACITY = 0.55;

export const selectNonPublishedDevicesAsPixPolygons = createSelector(
  [selectNonPublishedDevices],
  (nonPublischedDevicesFromAlgos) => {
    return convertDevicesToPixPolygons({
      devices: nonPublischedDevicesFromAlgos,
      selectedDeviceId: null,
      typeDevices: [],
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
      rooms: {},
    }).map((polygon) => ({
      ...polygon,
      opacity: NON_PUBLISHED_DEVICE_OPACITY,
    }));
  }
);

// const { data } = openapi.endpoints.postMcbimItems.useQuery(args ?? skipToken);

/** Hämtar query-resultatet (senaste i cachen) för nuvarande args */
// stabila tom-referenser
const EMPTY_TYPES: GetMultiplacerDevicetypesApiResponse = [];

// Memoisera och extrahera bara data
export const selectHttpGetMultiplacerDeviceTypesData = createSelector(
  [selectProjectNo, (s: IRootState) => s],
  (projectNo, state) =>
    openapi.endpoints.getMultiplacerDevicetypes.select({ projectNo })(state).data ?? EMPTY_TYPES
);

export const { setSavingDevices, setSelectedDeviceId, setDeviceIdInCtrlCInRedux } =
  deviceSlice.actions;
